import { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { Button, Card, LoadingSpinner, ErrorMessage, AddressAutocomplete, DecimalTextField, PhoneField } from "@/components/ui";
import { DEFAULT_PHONE_DIAL_CODE } from "@/constants/phoneDialCodes";
import {
  enterpriseService,
  CreateEnterpriseData,
  EnterpriseDiscountRule,
  DiscountType,
} from "@/services/enterprise.service";
import { useToastStore } from "@/store/toastStore";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store/authStore";

// Schéma de validation Zod (vatRate en % 0–100 pour l'affichage)
const enterpriseSchema = z.object({
  name: z.string().min(1, "Le nom est obligatoire"),
  siret: z.string().optional(),
  vatNumber: z.string().optional(),
  vatRatePercent: z
    .number()
    .min(0, "Le taux de TVA doit être positif ou nul")
    .max(100, "Le taux de TVA ne peut pas dépasser 100 %")
    .optional()
    .nullable(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  phoneDialCode: z.string().default(DEFAULT_PHONE_DIAL_CODE),
  phone: z.string().optional(),
  email: z.string().email("Email invalide").optional().or(z.literal("")),
  contactPerson: z.string().optional(),
  notes: z.string().optional(),
  isActive: z.boolean().default(true),
});

type EnterpriseFormData = z.infer<typeof enterpriseSchema>;

export function EnterpriseFormPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const isEditing = !!id;
  const fromSociety = (location.state as { fromSociety?: boolean } | null)?.fromSociety === true;

  useEffect(() => {
    if (fromSociety && user?.role !== "ADMIN") {
      navigate("/society", { replace: true });
    }
  }, [fromSociety, user?.role, navigate]);
  const { success, error: showError } = useToastStore();

  // Récupérer l'entreprise si édition (avec règles de réduction)
  const {
    data: enterprise,
    isLoading: enterpriseLoading,
    error: enterpriseError,
  } = useQuery({
    queryKey: ["enterprises", id],
    queryFn: () => enterpriseService.getById(id!, true),
    enabled: isEditing,
  });

  // État local du tableau de réduction (éditable)
  const [discountRules, setDiscountRules] = useState<Array<Omit<EnterpriseDiscountRule, "id" | "enterpriseId">>>([]);
  const [discountRulesTouched, setDiscountRulesTouched] = useState(false);
  useEffect(() => {
    if (enterprise?.discountRules?.length) {
      setDiscountRules(
        enterprise.discountRules.map((r) => ({
          label: r.label,
          indexMin: r.indexMin,
          indexMax: r.indexMax,
          discountType: r.discountType as DiscountType,
          discountValue: Number(r.discountValue),
          minChargeAmount: Number(r.minChargeAmount),
          fixedDeliveryPrice: r.fixedDeliveryPrice != null ? Number(r.fixedDeliveryPrice) : undefined,
          displayOrder: r.displayOrder,
        }))
      );
    } else if (enterprise && !enterprise.discountRules?.length) {
      setDiscountRules([]);
    }
  }, [enterprise?.id, enterprise?.discountRules]);

  // Formulaire
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<EnterpriseFormData>({
    resolver: zodResolver(enterpriseSchema),
    defaultValues: {
      name: "",
      siret: "",
      vatNumber: "",
      vatRatePercent: null as number | null,
      address: "",
      postalCode: "",
      city: "",
      phoneDialCode: DEFAULT_PHONE_DIAL_CODE,
      phone: "",
      email: "",
      contactPerson: "",
      notes: "",
      isActive: true,
    },
  });

  // Remplir le formulaire avec les données si édition
  useEffect(() => {
    if (enterprise) {
      reset({
        name: enterprise.name,
        siret: enterprise.siret || "",
        vatNumber: enterprise.vatNumber || "",
        vatRatePercent:
          enterprise.vatRate != null ? Math.round(Number(enterprise.vatRate) * 1000) / 10 : null,
        address: enterprise.address || "",
        postalCode: enterprise.postalCode || "",
        city: enterprise.city || "",
        phoneDialCode: enterprise.phoneDialCode || DEFAULT_PHONE_DIAL_CODE,
        phone: enterprise.phone || "",
        email: enterprise.email || "",
        contactPerson: enterprise.contactPerson || "",
        notes: enterprise.notes || "",
        isActive: enterprise.isActive,
      });
    }
  }, [enterprise, reset]);

  // Mutation pour créer
  const createMutation = useMutation({
    mutationFn: (data: CreateEnterpriseData) => enterpriseService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprises"] });
      success("Entreprise créée avec succès");
      navigate("/enterprises");
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || "Erreur lors de la création de l'entreprise");
    },
  });

  // Mutation pour mettre à jour
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateEnterpriseData> }) =>
      enterpriseService.update(id, data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["enterprises"] });
      queryClient.invalidateQueries({ queryKey: ["enterprises", id] });
      success("Entreprise modifiée avec succès");
      try {
        const me = await authService.getMe();
        setUser(me);
      } catch {
        // Ignorer si l'utilisateur n'est plus connecté
      }
      navigate(fromSociety ? "/society" : "/enterprises");
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || "Erreur lors de la modification de l'entreprise");
    },
  });

  const setDiscountRulesMutation = useMutation({
    mutationFn: (rules: Array<Omit<EnterpriseDiscountRule, "id" | "enterpriseId">>) =>
      enterpriseService.setDiscountRules(id!, rules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprises", id] });
      setDiscountRulesTouched(false);
      success("Règles de réduction enregistrées");
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || "Erreur lors de l'enregistrement des règles");
    },
  });

  const addDiscountRule = () => {
    setDiscountRules((prev) => [
      ...prev,
      {
        label: "",
        indexMin: 0,
        indexMax: 0,
        discountType: "FIXED" as DiscountType,
        discountValue: 0,
        minChargeAmount: 0,
        fixedDeliveryPrice: undefined as number | undefined,
        displayOrder: prev.length,
      },
    ]);
    setDiscountRulesTouched(true);
  };

  const updateDiscountRule = (index: number, field: string, value: number | string | undefined) => {
    setDiscountRules((prev) => {
      const next = [...prev];
      (next[index] as Record<string, unknown>)[field] = value;
      return next;
    });
    setDiscountRulesTouched(true);
  };

  const removeDiscountRule = (index: number) => {
    setDiscountRules((prev) => prev.filter((_, i) => i !== index));
    setDiscountRulesTouched(true);
  };

  const saveDiscountRules = () => {
    const valid = discountRules.every(
      (r) =>
        r.label.trim() !== "" &&
        r.indexMin <= r.indexMax &&
        r.discountValue >= 0 &&
        r.minChargeAmount >= 0 &&
        (r.fixedDeliveryPrice == null || r.fixedDeliveryPrice >= 0)
    );
    if (!valid) {
      showError("Vérifiez les champs (libellé, indice min ≤ max, montants ≥ 0)");
      return;
    }
    setDiscountRulesMutation.mutate(discountRules);
  };

  const onSubmit = (data: EnterpriseFormData) => {
    const enterpriseData: CreateEnterpriseData = {
      name: data.name,
      siret: data.siret || undefined,
      vatNumber: data.vatNumber || undefined,
      vatRate:
        data.vatRatePercent != null
          ? Number((Number(data.vatRatePercent) / 100).toFixed(4))
          : null,
      address: data.address || undefined,
      postalCode: data.postalCode || undefined,
      city: data.city || undefined,
      phoneDialCode: data.phoneDialCode || DEFAULT_PHONE_DIAL_CODE,
      phone: data.phone || undefined,
      email: data.email || undefined,
      contactPerson: data.contactPerson || undefined,
      notes: data.notes || undefined,
      isActive: data.isActive,
    };

    if (isEditing) {
      updateMutation.mutate({ id: id!, data: enterpriseData });
    } else {
      createMutation.mutate(enterpriseData);
    }
  };

  if (fromSociety && user?.role !== "ADMIN") {
    return <></>;
  }

  if (isEditing && enterpriseLoading) {
    return <LoadingSpinner message="Chargement de l'entreprise..." />;
  }

  if (isEditing && enterpriseError) {
    return <ErrorMessage message="Erreur lors du chargement de l'entreprise." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Titre */}
      <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
        {fromSociety ? "Fiche société" : isEditing ? "Modifier l'entreprise" : "Nouvelle entreprise"}
      </Typography>

      {/* Formulaire */}
      <Card>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Grid container spacing={3}>
            {/* Nom */}
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom de l'entreprise"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                  />
                )}
              />
            </Grid>

            {/* SIRET & TVA */}
            <Grid item xs={12} md={6}>
              <Controller
                name="siret"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Numéro SIRET" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="vatNumber"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Numéro de TVA" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="vatRatePercent"
                control={control}
                render={({ field }) => (
                  <DecimalTextField
                    {...field}
                    label="Taux de TVA (%)"
                    value={field.value}
                    onChange={field.onChange}
                    optional
                    emptyValue={null}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    fullWidth
                    helperText="0 à 100 (ex. 8,5 ou 20). 0 % autorisé."
                    error={!!errors.vatRatePercent}
                  />
                )}
              />
            </Grid>

            {/* Personne de contact */}
            <Grid item xs={12} md={6}>
              <Controller
                name="contactPerson"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Personne de contact" fullWidth />
                )}
              />
            </Grid>
            
            {/* Email */}
            <Grid item xs={12} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    fullWidth
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            {/* Téléphone */}
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneDialCode"
                control={control}
                render={({ field: dialField }) => (
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field: phoneField }) => (
                      <PhoneField
                        dialCode={dialField.value}
                        phone={phoneField.value ?? ""}
                        onDialCodeChange={dialField.onChange}
                        onPhoneChange={phoneField.onChange}
                        size="medium"
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Adresse (avec autocomplétion) */}
            <Grid item xs={12}>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <AddressAutocomplete
                    address={field.value ?? ""}
                    postalCode={watch("postalCode") ?? ""}
                    city={watch("city") ?? ""}
                    onAddressChange={({ address, postalCode, city }) => {
                      field.onChange(address);
                      setValue("postalCode", postalCode ?? "");
                      setValue("city", city ?? "");
                    }}
                    label="Adresse"
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Code postal et Ville */}
            <Grid item xs={12} md={4}>
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Code postal" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Ville" fullWidth />
                )}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Informations supplémentaires..."
                  />
                )}
              />
            </Grid>

            {/* Statut actif */}
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Entreprise active"
                  />
                )}
              />
            </Grid>

          </Grid>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="text"
              onClick={() => navigate(fromSociety ? "/dashboard" : "/enterprises")}
              disabled={isSubmitting}
            >
              {fromSociety ? "Retour" : "Annuler"}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
            >
              {isEditing ? "Enregistrer les modifications" : "Créer l'entreprise"}
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Tableau de réduction (édition uniquement) */}
      {isEditing && id && (
        <Card sx={{ p: 2 }}>
          <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 700, mb: 2 }}>
            Tableau de réduction
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Définissez des règles par tranche d'indice client. Sur la fiche client, l'indice du client détermine la règle appliquée (indice entre minimum et maximum). En prise de commande, la réduction s'applique selon le type (fixe ou %) avec un reste à charge minimum.
            {" "}
            Le <strong>prix fixe livraison</strong>, s&apos;il est renseigné, remplace le tarif de la zone pour une{" "}
            <strong>commande livrée</strong> : c&apos;est un <strong>forfait unique par commande</strong>, quel que soit le nombre de plats.
          </Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Libellé</TableCell>
                <TableCell align="right">Indice min</TableCell>
                <TableCell align="right">Indice max</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Réduction</TableCell>
                <TableCell align="right">Reste à charge min (€)</TableCell>
                <TableCell align="right">Prix fixe livraison (€)</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {discountRules.map((rule, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={rule.label}
                      onChange={(e) => updateDiscountRule(index, "label", e.target.value)}
                      placeholder="Ex. Tranche 1"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 80 }}
                      value={rule.indexMin}
                      onChange={(e) => updateDiscountRule(index, "indexMin", parseInt(e.target.value, 10) || 0)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 80 }}
                      value={rule.indexMax}
                      onChange={(e) => updateDiscountRule(index, "indexMax", parseInt(e.target.value, 10) || 0)}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={rule.discountType}
                      onChange={(e) => updateDiscountRule(index, "discountType", e.target.value as DiscountType)}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="FIXED">Fixe (€)</MenuItem>
                      <MenuItem value="PERCENTAGE">Pourcentage (%)</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <DecimalTextField
                      size="small"
                      sx={{ width: 90 }}
                      value={rule.discountValue}
                      onChange={(v) =>
                        updateDiscountRule(index, "discountValue", v ?? 0)
                      }
                      placeholder={rule.discountType === "FIXED" ? "14" : "50"}
                    />
                    {rule.discountType === "FIXED" ? " €" : " %"}
                  </TableCell>
                  <TableCell align="right">
                    <DecimalTextField
                      size="small"
                      sx={{ width: 90 }}
                      value={rule.minChargeAmount}
                      onChange={(v) =>
                        updateDiscountRule(index, "minChargeAmount", v ?? 0)
                      }
                      placeholder="3,5"
                    />
                    €
                  </TableCell>
                  <TableCell align="right">
                    <DecimalTextField
                      size="small"
                      sx={{ width: 90 }}
                      value={rule.fixedDeliveryPrice ?? undefined}
                      onChange={(v) =>
                        updateDiscountRule(index, "fixedDeliveryPrice", v ?? undefined)
                      }
                      placeholder="—"
                    />
                    {rule.fixedDeliveryPrice != null ? " €" : ""}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeDiscountRule(index)} aria-label="Supprimer">
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button variant="outline" size="small" startIcon={<Add />} onClick={addDiscountRule}>
              Ajouter une ligne
            </Button>
            {discountRulesTouched && (
              <Button
                variant="primary"
                size="small"
                onClick={saveDiscountRules}
                disabled={setDiscountRulesMutation.isPending}
              >
                Enregistrer les règles
              </Button>
            )}
          </Box>
        </Card>
      )}
    </Box>
  );
}
